import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify"; // ⚡ New: Using react-toastify
import Table from "../../../core/common/dataTable/index";
import {
  clearError,
  deleteNOCOutjobRequest,
  fetchNOCOutjobRequests,
  NOCOutjobRequest,
  setCurrentRequest,
} from "../../../core/data/redux/nocOutjobSlice";
import DeleteModal from "../../../core/modals/deleteModal";
import { formatEmpIdForDisplay } from "../../../utils/empIdUtils";
import { all_routes } from "../../router/all_routes";
import SyncNocRequest from "./sync-noc-request";
import DateRangeFilter from "../../../core/common/DateRangeFilter";
import RequestStatusBadge from "./RequestStatusBadge";
import RequestStatusFilter from "./RequestStatusFilter";
import { isAdminOrSubAdmin, isSupportViewer } from "../../../utils/permissionUtils";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import { processNocDownloadResponse } from "../../../utils/nocDownloadUtils";

interface NOCOutsideJobProps { }

const user: any = localStorage.getItem("user");
const userJson = user && user != "undefined" ? JSON.parse(user) : {};
let userType: any = userJson?.user_type || userJson?.role;
const isManagement = isAdminOrSubAdmin(userType) || isSupportViewer(userType);

// --- Utility Functions (Standardized) ---

const formatDate = (dateString: string | null) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (e) {
    return "-";
  }
};

// --- Component ---

const NOCOutsideJob = (props: NOCOutsideJobProps) => {
  const dispatch = useDispatch<any>();
  const routes = all_routes;
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 10,
  });
  // Redux state
  const { requests, loading, pagination: pageData, error, deleteLoading } = useSelector(
    (state: any) => state.nocOutjobState
  );
  useEffect(() => {
    console.log("requests:", requests);
    if (pageData) {
      setPagination({
        current: pageData.current || 1,
        pageSize: pageData.pageSize || 10,
        total: pageData.total || 0,
      });
    }
  }, [pageData]);



  // Local state
  const [deleteModalId, setDeleteModalId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [downloadingRecordId, setDownloadingRecordId] = useState<number | null>(null);

  const [dataList, setDataList] = useState([]);

  useEffect(() => {
    if (requests) {
      setDataList(requests);
    }
  }, [requests]);

  console.log("dataList:", dataList);

  // Data fetching logic
  const fetchData = (
    page = pagination.current,
    limit = pagination.pageSize,
    query = searchQuery,
    requestStatus = selectedStatus,
    dateStart = startDate,
    dateEnd = endDate
  ) => {
    console.log("Fetching data with:", { page, limit, query, dateStart, dateEnd });
    const params: any = {
      page,
      limit,
      query,
      orderColumn: "created_at",
      orderBy: "DESC",
    };
    if (dateStart) params.startDate = dateStart;
    if (dateEnd) params.endDate = dateEnd;
    if (requestStatus) params.requestStatus = requestStatus;
    dispatch(fetchNOCOutjobRequests(params));
  };

  // Fetch data on component mount/pagination change
  useEffect(() => {
    fetchData(pagination.current, pagination.pageSize);
  }, []);

  // ⚡ New: Toast for Redux errors
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Handle pagination change
  // const handlePagination = (page: number, pageSize?: number) => {
  //   fetchData(page, pageSize || pagination.limit, searchQuery);
  // };

  const handlePagination = (newPagination: any) => {
    setPagination((prev) => {
      const nextPageSize = newPagination.pageSize || prev.pageSize || 10;
      const nextCurrent =
        newPagination.pageSize && newPagination.pageSize !== prev.pageSize
          ? 1
          : newPagination.current || 1;

      const updatedPagination = {
        current: nextCurrent,
        pageSize: nextPageSize,
        total: newPagination.total || prev.total || 0,
      };

      console.log("prev.pageSize", prev.pageSize);
      console.log("newPagination.pageSize", newPagination.pageSize);
      console.log("Updated Pagination", updatedPagination);

      fetchData(updatedPagination.current, updatedPagination.pageSize, searchQuery, selectedStatus, startDate, endDate);
      return updatedPagination;
    });
  };





  // Handle search action
  const debouncedFetchData = useCallback(
    debounce((page: number, limit: number, query: string) => {
      fetchData(page, limit, query);
    }, 500), // 500ms delay
    []
  );

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    debouncedFetchData(1, pagination.pageSize, query); // ⚡ Resets to page 1 on new search with debounce
  };

  // Handle filter apply
  const handleApplyFilter = () => {
    // Reset to first page when applying filters
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData(1, pagination.pageSize, searchQuery, selectedStatus, startDate, endDate);
  };

  // Handle clear filter
  const handleClearFilter = () => {
    setStartDate("");
    setEndDate("");
    setSelectedStatus("");
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData(1, pagination.pageSize, searchQuery, "", "", "");
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedFetchData.cancel();
    };
  }, [debouncedFetchData]);

  // Handle delete confirmation
  const handleDeleteClick = (id: number) => {
    setDeleteModalId(id);
  };

  // Handle delete confirmation response
  const handleDeleteConfirm = async (response: string) => {
    if (response === "YES" && deleteModalId) {
      try {
        await dispatch(deleteNOCOutjobRequest({ id: deleteModalId })).unwrap();
        toast.success("NOC request deleted successfully!"); // ⚡ Using toastify
        fetchData(pagination.current, pagination.pageSize, searchQuery, selectedStatus, startDate, endDate); // Re-fetch current page
      } catch (error) {
        console.error("Failed to delete request:", error);
        toast.error("Failed to delete NOC request. Please try again."); // ⚡ Using toastify
      }
    }
    setDeleteModalId(null);
  };

  // Handle view details click
  const handleViewDetailsClick = (record: NOCOutjobRequest) => {
    dispatch(setCurrentRequest(record.id));
  };

  // Handle edit button click
  const handleEditClick = (record: NOCOutjobRequest) => {
    dispatch(setCurrentRequest(record.id));
  };

  console.log("pageData:", pageData);
  // Columns definition matching SAP Outside Job Previous Requests
  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      width: 70,
      render: (_: any, __: any, index: number) => (
        <div>
          {pageData.page
            ? index +
            pageData.page * pageData.limit -
            pageData.limit +
            1
            : index + 1}
        </div>
      ),
    },
    {
      title: "Reference Number",
      dataIndex: "referenceNumber",
      sorter: (a: NOCOutjobRequest, b: NOCOutjobRequest) =>
        (a.referenceNumber || "").localeCompare(b.referenceNumber || ""),
      render: (text: string) => (
        <span className="fw-medium font-monospace">{text || "-"}</span>
      ),
    },
    {
      title: "Post Applied for",
      dataIndex: "postAppliedFor",
      sorter: (a: any, b: any) =>
        (a.postAppliedFor || (a as any).PostAppliedFor || "").localeCompare(
          b.postAppliedFor || (b as any).PostAppliedFor || ""
        ),
      render: (text: string, record: any) =>
        text || record.PostAppliedFor || record.postAppliedfor || "-",
    },
    {
      title: "Organisation in which",
      dataIndex: "organisationInWhich",
      sorter: (a: any, b: any) =>
        (a.organisationInWhich || (a as any).OrganisationInWhich || a.organisation || "").localeCompare(
          b.organisationInWhich || (b as any).OrganisationInWhich || b.organisation || ""
        ),
      render: (text: string, record: any) =>
        text || record.OrganisationInWhich || record.organisation || "-",
    },
    {
      title: "Advertisement No.",
      dataIndex: "advertisementNo",
      sorter: (a: any, b: any) =>
        (a.advertisementNo || (a as any).AdvertisementNo || "").localeCompare(
          b.advertisementNo || (b as any).AdvertisementNo || ""
        ),
      render: (text: string, record: any) =>
        text || record.AdvertisementNo || "-",
    },
    {
      title: "Application no. in year",
      dataIndex: "appNoInYear",
      render: (text: string, record: any) =>
        text || record.AppNoInYear || record.applicationNoInYear || record.app_no_in_year || "-",
    },
    {
      title: "D&AR Status",
      dataIndex: "dandARStatus",
      render: (text: string, record: any) => {
        const val = text || record.dnARStatus || record.DnARStatus || record.DandARStatus || record.dandARStatus;
        if (!val) return "-";
        if (val === "C" || val === "D&AR comp..." || val?.toLowerCase()?.includes("comp")) return "D&AR completed";
        if (val === "P" || val?.toLowerCase()?.includes("pend")) return "D&AR pending";
        if (val === "M") return "Major Penalty";
        if (val === "J") return "Minor Penalty";
        if (val === "N") return "No D&AR Case";
        return val;
      },
    },
    {
      title: "D&AR penalty effecti",
      dataIndex: "dandARPenaltyEffective",
      render: (text: string, record: any) => {
        const val = text || record.dnARPenaltyEffective || record.DnARPenaltyEffecti || record.DnARPenaltyEffective || record.dandARPenaltyEffective;
        return formatDate(val);
      },
    },
    {
      title: "Remarks by Controlling",
      dataIndex: "remarks",
      render: (text: string, record: any) =>
        text || record.Remarks || record.controllingRemarks || "-",
    },
    {
      title: "Remarks if rejected",
      dataIndex: "remarks1",
      render: (text: string, record: any) => {
        const status = String(record?.requestStatus || record?.status || "").toUpperCase();
        const isRejected = status === "R" || status === "REJECTED";
        if (!isRejected) return "-";
        const val = text || record.Remarks1 || record.hRemarks || record.HRemarks;
        return val || "-";
      },
    },
    {
      title: "Request Creation Date",
      dataIndex: "created_at",
      sorter: (a: any, b: any) =>
        new Date(a.requestCreationDate || a.created_at || a.requestCreationDat || 0).getTime() -
        new Date(b.requestCreationDate || b.created_at || b.requestCreationDat || 0).getTime(),
      render: (text: string, record: any) =>
        formatDate(record.requestCreationDate || record.created_at || record.requestCreationDat || record.sap_created_at),
    },
    {
      title: "Request Status",
      dataIndex: "requestStatus",
      render: (status: string, record: any) => (
        <RequestStatusBadge status={status || record.status} />
      ),
    },
    {
      title: "Last/Latest Approver",
      dataIndex: "approverName",
      render: (text: string, record: any) => {
        const name = text || record.lastApprover || record.approver?.name || record.approverName;
        const id = record.approverUserID || record.approverId || record.approver?.empId;
        if (name && id) return `${name} (${formatEmpIdForDisplay(id)})`;
        if (name) return name;
        if (id) return formatEmpIdForDisplay(id);
        return "-";
      },
    },
    {
      title: "Final D&AR/Vigilance",
      dataIndex: "finalDandARVigilance",
      render: (text: string, record: any) =>
        text || record.FinalDandARVigilance || record.finalDnarVigilance || record.FinalDnARVigilance || "-",
    },
    {
      title: "Reason for Rejection(if rejected)",
      dataIndex: "reasonForRejection",
      render: (text: string, record: any) => {
        const status = String(record?.requestStatus || record?.status || "").toUpperCase();
        const isRejected = status === "R" || status === "REJECTED";
        if (!isRejected) return "-";

        const rawVal = String(text || record.ReasonForRejection || record.reason_for_rejection || "").trim();
        const reasonsMap: Record<string, string> = {
          "1": "Incomplete details or documentation",
          "2": "Not eligible as per DMRC policy",
          "3": "Under D&AR/Vigilance proceedings",
          "4": "Other administrative reason",
        };

        if (rawVal && rawVal !== "0" && rawVal !== "00" && rawVal !== "null" && rawVal !== "undefined") {
          return reasonsMap[rawVal] || rawVal;
        }

        // If code is "0" / empty but remarks exist for rejected request, show remarks or "-"
        const rejectionRemarks = record.Remarks1 || record.remarks1 || record.hRemarks || record.HRemarks;
        return rejectionRemarks || "-";
      },
    },
  ];

  // Always include Actions column so users can view/edit draft requests
  columns.push({
    title: "Actions",
    dataIndex: "actions",
    render: (text: string, record: NOCOutjobRequest) => {
      const isDraft = Boolean(
        record.is_draft ||
        (record as any).isDraft ||
        record.requestStatus === "DRAFT" ||
        record.requestStatus === "draft" ||
        record.requestStatus === "-" ||
        !record.requestStatus
      );

      const isCompleted =
        ["D"].includes(String(record?.status || "").toUpperCase()) ||
        ["D"].includes(String(record?.requestStatus || "").toUpperCase()) ||
        String(record?.status || "").toLowerCase().includes("D") ||
        String(record?.requestStatus || "").toLowerCase().includes("D") ||
        String(record?.status || "").toLowerCase().includes("D") ||
        String(record?.requestStatus || "").toLowerCase().includes("D");

      const handleDownloadNocDoc = async (rec: any) => {
        if (downloadingRecordId === rec.id) return;
        try {
          setDownloadingRecordId(rec.id);
          const payload = {
            empId: rec.empID || rec.empId,
            type: "ZNOJ",
            referenceNumber: rec.referenceNumber,
          };
          const res = await Api.post(API_URLS.NOC.OUTSIDE_JOB_DOWNLOAD, payload);
          processNocDownloadResponse(res?.data);
        } catch (err: any) {
          toast.error(err?.message || "Failed to download NOC document.");
        } finally {
          setDownloadingRecordId(null);
        }
      };

      const isDownloading = downloadingRecordId === record.id;

      return (
        <div className="action-icon d-inline-flex align-items-center">
          <Link
            to={`${routes.noc.outsideJob}/details/${record.id}`}
            className="text-primary me-2"
            title="View Details"
            onClick={() => handleViewDetailsClick(record)}
          >
            <i className="ti ti-eye fs-16"></i>
          </Link>
          {isCompleted && (
            <button
              type="button"
              className="btn btn-link text-info p-0 me-2"
              title="Download Digital NOC"
              onClick={() => handleDownloadNocDoc(record)}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              ) : (
                <i className="ti ti-file-text fs-16"></i>
              )}
            </button>
          )}
          {isDraft && (
            <>
              <Link
                to={`${routes.noc.outsideJob}/edit/${record.id}`}
                className="text-success me-2"
                title="Edit Request"
                onClick={() => handleEditClick(record)}
              >
                <i className="ti ti-edit fs-16"></i>
              </Link>
              <button
                type="button"
                className="btn btn-link text-danger p-0"
                title="Delete Request"
                onClick={() => handleDeleteClick(record.id)}
                disabled={deleteLoading}
                data-bs-toggle="modal"
                data-bs-target="#delete_modal"
              >
                <i className="ti ti-trash fs-16"></i>
              </button>
            </>
          )}
        </div>
      );
    },
  });

  // Clear error on component unmount
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">NOC for Outside Job</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.main}>NOC Services</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Outside Job
                </li>
              </ol>
            </nav>
          </div>
        </div>
        {/* ⚡ Removed inline error display, now handled by useEffect/toastify */}

        <div className="card">
          {/* Card Header: Title on Left, Action Buttons on Right */}
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-3 py-3">
            <h4 className="mb-0 fw-bold">NOC Applications</h4>
            <div className="d-flex align-items-center gap-2">
              {!isManagement && (
                <Link
                  to={`${routes.noc.outsideJob}/create`}
                  className="btn btn-primary text-nowrap"
                >
                  <i className="ti ti-plus me-1"></i>
                  Create New Request
                </Link>
              )}
              <SyncNocRequest nocType={"NOC_OUTJOB"} onSyncSuccess={() => fetchData()} />
            </div>
          </div>

          <div className="card-body">
            {/* Dedicated Modern Filter Toolbar */}
            <div className="p-3 bg-light rounded-3 border mb-3">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <RequestStatusFilter
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                  />
                  <DateRangeFilter
                    startDate={startDate}
                    endDate={endDate}
                    onChangeStart={setStartDate}
                    onChangeEnd={setEndDate}
                    onApply={handleApplyFilter}
                    onClear={handleClearFilter}
                    variant="inline"
                  />
                </div>
              </div>
            </div>

            <Table
              columns={columns}
              dataSource={dataList}
              Selection={false}
              paginationConfig={pageData}
              handlePagination={handlePagination}
              loading={loading}
              handleSearch={handleSearch}
            />
          </div>
        </div>

        {/* Delete Modal */}
        <DeleteModal handleDelete={handleDeleteConfirm} />

        {/* ⚡ Removed custom Toasts component */}
      </div>
    </div>
  );
};

export default NOCOutsideJob;
